<template>
  <section id="hero" class="hero">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="tsparticles" :init="particlesInit" :options="particleConfig" />

    <div class="hero-layout">
      <div class="hero-left" data-aos="fade-right">
        <div class="eyebrow">
          <span class="eyebrow-line"></span>
          <span class="eyebrow-text">Evento Oficial · Grappling Pro</span>
        </div>
        <h1 class="hero-title">
          <span class="title-main">Are You</span>
          <span class="title-accent">Ready?</span>
        </h1>
        <p class="hero-body">
          Asegura tu lugar en el tatami antes de que los cupos se agoten. <br> Un evento
          diseñado para los que van en serio.
        </p>
        <div class="cupo-block">
          <div class="cupo-header">
            <span class="cupo-label">Cupos disponibles</span>
            <span class="cupo-pct">{{ Math.round(displayedPercentage) }}% ocupado</span>
          </div>
          <div class="cupo-track">
            <div class="cupo-fill" :style="{ width: displayedPercentage + '%' }">
              <span class="cupo-shine"></span>
            </div>
          </div>
          <p class="cupo-aviso" v-if="evento?.porcentaje_cupo >= 75">
            ⚡ Últimos lugares disponibles
          </p>
        </div>
        <a href="/comprar-boletos" class="cta-btn">
          <span class="cta-text">Comprar boletos</span>
          <span class="cta-arrow">→</span>
        </a>
      </div>

      <div class="hero-right" data-aos="fade-left" data-aos-delay="150">
        <div class="video-frame">
          <div class="video-corner tl"></div>
          <div class="video-corner tr"></div>
          <div class="video-corner bl"></div>
          <div class="video-corner br"></div>
          <div ref="videoContainer" class="video-inner">
            <iframe
              v-if="showVideo && evento?.video_promocional_url"
              :src="evento.video_promocional_url"
              title="Evolve Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
            <div v-else class="video-placeholder">
              <div class="play-icon">▶</div>
              <p>Cargando video del evento...</p>
            </div>
          </div>
          <div class="video-tag">Evolve Grappling Pro</div>
        </div>
        <a href="/registro-peleador" class="cta-btn cta-btn--under-video">
          <span class="cta-text">Quiero competir</span>
          <span class="cta-arrow">→</span>
        </a>
      </div>
    </div>
  </section>

  <!-- ══ CARTELERA DE COMBATES ══════════════════════════════════════════════ -->
  <section class="cartelera-seccion" v-if="grupos.length > 0">
    <div class="cartelera-bg" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="cartelera-gradient"></div>
    <Particles id="carteleraParticles" :init="particlesInit" :options="particleConfig" />

    <div class="cartelera-wrap">
      <!-- Header -->
      <div class="cartelera-header" data-aos="fade-up">
        <span class="cartelera-linea"></span>
        <div class="cartelera-titulo-wrap">
          <span class="cartelera-eyebrow">Evolve Grappling Pro</span>
          <h2 class="cartelera-titulo">Cartelera <em>de Combates</em></h2>
        </div>
        <span class="cartelera-linea cartelera-linea--right"></span>
      </div>

      <!-- Grupos por categoría -->
      <div
        v-for="grupo in grupos"
        :key="grupo.categoria"
        class="categoria-grupo"
        data-aos="fade-up"
        data-aos-delay="80"
      >
        <!-- Separador de categoría -->
        <div class="cat-sep" :class="`cat-sep--${grupo.categoria}`">
          <span
            class="cat-sep-linea cat-sep-linea--izq"
            :class="`cat-sep-linea--${grupo.categoria}`"
          ></span>
          <span class="cat-sep-label" :class="`cat-sep-label--${grupo.categoria}`">
            {{ labelCategoria(grupo.categoria) }}
          </span>
          <span
            class="cat-sep-linea cat-sep-linea--der"
            :class="`cat-sep-linea--${grupo.categoria}`"
          ></span>
        </div>

        <!-- Lista centrada: un combate por renglón -->
        <div class="combates-grid">
          <div v-for="combate in grupo.combates" :key="combate.id" class="combate-wrap">
            <!-- Número de combate (badge superior centrado, FUERA del blur) -->
            <span class="combate-num" v-if="combate.numero_combate">
              Pelea {{ String(combate.numero_combate).padStart(2, "0") }}
            </span>

            <div
              class="combate-row"
              :class="{
                'combate-row--estelar': grupo.categoria === 'estelar',
                'combate-row--coestelar': grupo.categoria === 'coestelar',
                'combate-row--main-card': grupo.categoria === 'main_card',
              }"
            >
              <!-- Peleador A (izquierda) -->
              <div class="peleador-cell peleador-cell--izq">
                <!-- Vista desktop: foto | info -->
                <div class="peleador-foto-wrap peleador-foto-wrap--desktop">
                  <img
                    v-if="combate.peleador_a.foto"
                    :src="combate.peleador_a.foto"
                    :alt="combate.peleador_a.nombre"
                    class="peleador-foto"
                  />
                  <div v-else class="peleador-foto-placeholder">
                    {{ initials(combate.peleador_a.nombre) }}
                  </div>
                </div>
                <div class="peleador-info peleador-info--izq peleador-info--desktop">
                  <span class="peleador-nombre">{{ combate.peleador_a.nombre }}</span>
                  <span class="peleador-equipo" v-if="combate.peleador_a.equipo">
                    {{ combate.peleador_a.equipo }}
                  </span>
                  <span class="peleador-nac" v-if="combate.peleador_a.nacionalidad_iso">
                    <img
                      :src="`https://flagcdn.com/w40/${combate.peleador_a.nacionalidad_iso.toLowerCase()}.png`"
                      :alt="combate.peleador_a.nacionalidad_iso"
                      class="bandera-img"
                    />
                    {{ combate.peleador_a.nacionalidad_display }}
                  </span>
                </div>
                <!-- Vista móvil: imagen con bandera superpuesta → nombre -->
                <div class="peleador-card peleador-card--movil">
                  <div class="peleador-foto-wrap peleador-foto-wrap--movil">
                    <img
                      v-if="combate.peleador_a.foto"
                      :src="combate.peleador_a.foto"
                      :alt="combate.peleador_a.nombre"
                      class="peleador-foto"
                    />
                    <div v-else class="peleador-foto-placeholder">
                      {{ initials(combate.peleador_a.nombre) }}
                    </div>
                    <!-- Bandera superpuesta: esquina inferior izquierda -->
                    <div
                      class="bandera-overlay bandera-overlay--izq"
                      v-if="combate.peleador_a.nacionalidad_iso"
                    >
                      <img
                        :src="`https://flagcdn.com/w40/${combate.peleador_a.nacionalidad_iso.toLowerCase()}.png`"
                        :alt="combate.peleador_a.nacionalidad_iso"
                        class="bandera-overlay-img"
                      />
                    </div>
                  </div>
                  <span class="peleador-nombre peleador-nombre--movil">{{
                    combate.peleador_a.nombre
                  }}</span>
                </div>
              </div>

              <!-- VS central -->
              <div class="vs-block">
                <span class="vs-texto">VS</span>
              </div>

              <!-- Peleador B (derecha) -->
              <div class="peleador-cell peleador-cell--der">
                <!-- Vista desktop: info | foto -->
                <div class="peleador-info peleador-info--der peleador-info--desktop">
                  <span class="peleador-nombre">{{ combate.peleador_b.nombre }}</span>
                  <span class="peleador-equipo" v-if="combate.peleador_b.equipo">
                    {{ combate.peleador_b.equipo }}
                  </span>
                  <span
                    class="peleador-nac peleador-nac--der"
                    v-if="combate.peleador_b.nacionalidad_iso"
                  >
                    <img
                      :src="`https://flagcdn.com/w40/${combate.peleador_b.nacionalidad_iso.toLowerCase()}.png`"
                      :alt="combate.peleador_b.nacionalidad_iso"
                      class="bandera-img"
                    />
                    {{ combate.peleador_b.nacionalidad_display }}
                  </span>
                </div>
                <div class="peleador-foto-wrap peleador-foto-wrap--desktop">
                  <img
                    v-if="combate.peleador_b.foto"
                    :src="combate.peleador_b.foto"
                    :alt="combate.peleador_b.nombre"
                    class="peleador-foto"
                  />
                  <div v-else class="peleador-foto-placeholder">
                    {{ initials(combate.peleador_b.nombre) }}
                  </div>
                </div>
                <!-- Vista móvil: imagen con bandera superpuesta → nombre -->
                <div class="peleador-card peleador-card--movil">
                  <div class="peleador-foto-wrap peleador-foto-wrap--movil">
                    <img
                      v-if="combate.peleador_b.foto"
                      :src="combate.peleador_b.foto"
                      :alt="combate.peleador_b.nombre"
                      class="peleador-foto"
                    />
                    <div v-else class="peleador-foto-placeholder">
                      {{ initials(combate.peleador_b.nombre) }}
                    </div>
                    <!-- Bandera superpuesta: esquina inferior derecha -->
                    <div
                      class="bandera-overlay bandera-overlay--der"
                      v-if="combate.peleador_b.nacionalidad_iso"
                    >
                      <img
                        :src="`https://flagcdn.com/w40/${combate.peleador_b.nacionalidad_iso.toLowerCase()}.png`"
                        :alt="combate.peleador_b.nacionalidad_iso"
                        class="bandera-overlay-img"
                      />
                    </div>
                  </div>
                  <span class="peleador-nombre peleador-nombre--movil">{{
                    combate.peleador_b.nombre
                  }}</span>
                </div>
              </div>
            </div>
            <!-- /combate-row -->
          </div>
          <!-- /combate-wrap -->
        </div>
        <!-- /combates-grid -->
      </div>
      <!-- /categoria-grupo -->

      <!-- CTA -->
      <div class="cartelera-cta" data-aos="fade-up">
        <a href="/peleadores" class="cta-btn cta-btn--under-video">
          <span class="cta-text">Lista de peleadores</span>
          <span class="cta-arrow">→</span>
        </a>
      </div>
    </div>
    <!-- /cartelera-wrap -->
  </section>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import heroImage from "@/assets/img/Evolve/img_8.jpg";
import { loadSlim } from "tsparticles-slim";
import { getEventoActivo, getCombatesPublicos } from "@/utils/api_evento";

const evento = ref(null);
const displayedPercentage = ref(0);
const showVideo = ref(false);
const videoContainer = ref(null);
const grupos = ref([]);

const fetchEventoActivo = async () => {
  try {
    evento.value = await getEventoActivo();
  } catch (error) {
    console.error("Error cargando evento:", error);
  }
};

const fetchCombates = async () => {
  try {
    grupos.value = await getCombatesPublicos();
  } catch (e) {
    console.error("Error cargando cartelera:", e);
  }
};

onMounted(async () => {
  fetchEventoActivo();
  fetchCombates();

  if (videoContainer.value) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          showVideo.value = true;
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(videoContainer.value);
  }
});

const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#cc0000" },
    shape: { type: "circle" },
    opacity: { value: 0.2 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};
const particlesInit = async (engine) => await loadSlim(engine);

watch(
  () => evento.value?.porcentaje_cupo,
  (newVal) => {
    if (!newVal) return;
    displayedPercentage.value = 0;
    const step = newVal / 40;
    const interval = setInterval(() => {
      if (displayedPercentage.value >= newVal) {
        displayedPercentage.value = newVal;
        clearInterval(interval);
      } else {
        displayedPercentage.value += step;
      }
    }, 40);
  },
  { immediate: true }
);

const initials = (nombre = "") =>
  nombre
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();

const LABEL_CATEGORIA = {
  estelar: "Estelares",
  coestelar: "Coestelares",
  main_card: "Main Cards",
  preliminar: "Preliminares",
  teens: "Teens",
  kids: "Kids",
};
const labelCategoria = (cat) => LABEL_CATEGORIA[cat] ?? cat;
</script>

<style scoped>
/* ══ HERO ═══════════════════════════════════════════════════════════════════ */
.hero {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
}
.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  /* filter: brightness(0.28) saturate(0.6); */
  z-index: 1;
}

.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    125deg,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(20, 4, 2, 0.5) 50%,
    rgba(0, 0, 0, 0.05) 100%
  );
  z-index: 2;
}
#tsparticles,
#carteleraParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}
.smoke-layer {
  position: absolute;
  inset: 0;
  background: src("/assets/backgrounds/image3.png");
  opacity: 0.18;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}
@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.2);
  }
  100% {
    transform: translateY(-20%) scale(1.25);
  }
}
.diagonal-accent {
  position: absolute;
  top: 0;
  right: 35%;
  width: 1px;
  height: 100%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(204, 0, 0, 0.2) 35%,
    rgba(160, 160, 170, 0.1) 65%,
    transparent 100%
  );
  transform: skewX(-8deg);
  z-index: 5;
}
.hero-layout {
  position: relative;
  z-index: 10;
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 60px;
  align-items: center;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 33px 60px 40px;
}
.hero-left {
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
}
.eyebrow-line {
  display: block;
  width: 32px;
  height: 2px;
  background: #cc0000;
}
.eyebrow-text {
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(180, 185, 195, 0.9);
}
.hero-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1;
  margin: 0;
}
.title-main {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(3.2rem, 5.5vw, 5rem);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
}
.title-accent {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(3.8rem, 7vw, 6rem);
  font-weight: 700;
  text-transform: uppercase;
  color: transparent;
  -webkit-text-stroke: 2px #cc0000;
  letter-spacing: -1px;
  filter: drop-shadow(0 0 24px rgba(204, 0, 0, 0.45))
    drop-shadow(0 0 6px rgba(200, 205, 215, 0.1));
}
.hero-body {
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.816);
  max-width: 400px;
  margin: 0;
  font-weight: 300;
}
.cupo-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 420px;
}
.cupo-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.cupo-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.816);
  font-weight: 600;
  flex: 1;
}
.cupo-pct {
  font-family: "Oswald", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
}
.cupo-track {
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.cupo-fill {
  position: relative;
  height: 100%;
  background: linear-gradient(90deg, #7a0000, #cc0000, #b0b6c0);
  border-radius: 3px;
  transition: width 0.4s ease-out;
  overflow: hidden;
}
.cupo-shine {
  position: absolute;
  top: 0;
  left: -40%;
  width: 35%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(220, 225, 235, 0.55), transparent);
  animation: shineMove 2.5s linear infinite;
}
@keyframes shineMove {
  0% {
    left: -40%;
  }
  100% {
    left: 130%;
  }
}
.cupo-aviso {
  font-size: 0.7rem;
  color: rgba(200, 205, 215, 0.8);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0;
  font-weight: 600;
}
.cta-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  align-self: flex-start;
  padding: 14px 30px;
  /* Sin border-radius — los cortes diagonales los hacemos con clip-path */
  border-radius: 0;
  color: #fff;
  text-decoration: none;
  font-family: "Oswald", sans-serif;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  border: 1px solid rgba(204, 0, 0, 0.65);
  background: linear-gradient(135deg, #7a0000 0%, #cc0000 65%, #a80000 100%);
  position: relative;
  overflow: hidden;
  transition: background 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  animation: pulseGlow 3s ease-in-out infinite;
  /* Corte diagonal: esquina superior-izquierda e inferior-derecha */
  clip-path: polygon(
    12px 0%,
    /* corte sup-izq */ 100% 0%,
    100% calc(100% - 12px),
    /* corte inf-der */ calc(100% - 12px) 100%,
    0% 100%,
    0% 12px /* vértice del corte sup-izq */
  );
}
/* Línea decorativa en los cortes */
.cta-btn::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  clip-path: polygon(
    12px 0%,
    100% 0%,
    100% calc(100% - 12px),
    calc(100% - 12px) 100%,
    0% 100%,
    0% 12px
  );
  pointer-events: none;
  z-index: 1;
}
.cta-btn::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.06) 0%,
    #cc0000 40%,
    #e60000 100%
  );
  opacity: 0;
  transition: opacity 0.35s ease;
  z-index: 0;
}
.cta-btn:hover {
  background: linear-gradient(135deg, #8a0000 0%, #dd0000 65%, #bb0000 100%);
  box-shadow: 0 0 22px rgba(204, 0, 0, 0.35);
}
.cta-btn:hover::before {
  opacity: 1;
}
.cta-btn:active {
  transform: scale(0.97);
  box-shadow: 0 4px 14px rgba(204, 0, 0, 0.35);
}
.cta-btn:focus-visible {
  outline: 2px solid #cc0000;
  outline-offset: 3px;
}
@keyframes pulseGlow {
  0%,
  100% {
    box-shadow: 0 0 0 rgba(204, 0, 0, 0);
  }
  50% {
    box-shadow: 0 0 20px rgba(204, 0, 0, 0.3), 0 0 6px rgba(180, 185, 195, 0.08);
  }
}
.cta-text,
.cta-arrow {
  position: relative;
  z-index: 1;
}
.cta-arrow {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}
.cta-btn:hover .cta-arrow {
  transform: translateX(5px);
}
.hero-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}
.video-frame {
  position: relative;
  width: 100%;
  max-width: 600px;
}
.video-corner {
  position: absolute;
  width: 18px;
  height: 18px;
  z-index: 2;
}
.video-corner.tl {
  top: -6px;
  left: -6px;
  border-top: 2px solid #cc0000;
  border-left: 2px solid #cc0000;
}
.video-corner.tr {
  top: -6px;
  right: -6px;
  border-top: 2px solid #9aa0aa;
  border-right: 2px solid #9aa0aa;
}
.video-corner.bl {
  bottom: -6px;
  left: -6px;
  border-bottom: 2px solid #9aa0aa;
  border-left: 2px solid #9aa0aa;
}
.video-corner.br {
  bottom: -6px;
  right: -6px;
  border-bottom: 2px solid #cc0000;
  border-right: 2px solid #cc0000;
}
.video-inner {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  border-radius: 6px;
  overflow: hidden;
  background: #0a0202;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(204, 0, 0, 0.1);
}
.video-inner iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}
.video-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.25);
}
.play-icon {
  font-size: 2.5rem;
  color: rgba(204, 0, 0, 0.45);
}
.video-placeholder p {
  font-size: 0.75rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0;
}
.video-tag {
  position: absolute;
  bottom: -11px;
  left: 20px;
  background: #cc0000;
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 3px 12px;
  border-radius: 2px;
}
.cta-btn--under-video {
  align-self: center;
  width: fit-content;
  justify-content: center;
  margin-top: 8px;
}

/* ── Hero responsive ── */
@media (max-width: 1024px) {
  .hero-layout {
    grid-template-columns: 1fr;
    gap: 36px;
    padding: 90px 32px 50px;
    text-align: center;
  }
  .hero-left {
    align-items: center;
  }
  .eyebrow {
    justify-content: center;
  }
  .hero-body {
    max-width: 540px;
  }
  .cupo-block {
    max-width: 480px;
  }
  .cta-btn {
    min-width: 240px;
    justify-content: center;
    align-self: center;
  }
  .diagonal-accent {
    display: none;
  }
  .video-frame {
    max-width: 640px;
  }
  .cta-btn--under-video {
    min-width: 240px;
    width: 100%;
    max-width: 260px;
  }
}
@media (max-width: 600px) {
  .hero-layout {
    padding: 30px 20px 40px;
    gap: 28px;
  }
  .title-main {
    font-size: 2.2rem;
  }
  .title-accent {
    font-size: 3rem;
  }
  .cta-btn {
    width: auto;
    min-width: 200px;
    max-width: 300px;
    justify-content: center;
    padding: 12px 24px;
    font-size: 0.85rem;
  }
  .cta-btn--under-video {
    max-width: 300px;
    width: 100%;
   
  
  }
}
@media (max-width: 480px) {

  .cupo-header {
    flex-wrap: wrap;
    gap: 6px;
  }

  .cupo-label {
    flex: 1;
  }

  .cupo-pct {
    white-space: nowrap;
  }

}
/* ══ CARTELERA ══════════════════════════════════════════════════════════════ */
.cartelera-seccion {
  position: relative;
  overflow: hidden;
  background: #08010100;
  padding: 0;
  margin: 0;
}
.cartelera-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  /* ✅ FIX: sin esto, background-size:cover escala la imagen respecto al
     alto de la sección — y esa sección crece con cada categoría/pelea que
     se agrega. Mientras más peleadores, más "zoom" le mete cover para
     cubrir todo el alto, y se ve cada vez más grande y distorsionada.
     background-attachment:fixed dimensiona el fondo respecto al viewport
     en vez de respecto al elemento, así el zoom queda fijo sin importar
     cuánto crezca el contenido. */
  background-attachment: fixed;
  /* filter: brightness(0.28) saturate(0.6); */
  z-index: 1;
}
.cartelera-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    125deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(20, 4, 2, 0.5) 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
  z-index: 2;
}
.cartelera-smoke {
  position: absolute;
  inset: 0;
  background: src("src/assets/backgrounds/image3.png");
  background-size: cover;
  opacity: 0.13;
  mix-blend-mode: lighten;
  animation: smokeMove 25s infinite linear;
  z-index: 4;
}
.cartelera-wrap {
  position: relative;
  z-index: 10;
  width: min(1200px, 92vw);
  margin: 0 auto;
  padding: 72px 0 80px;
}

/* ── Header ── */
.cartelera-header {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 52px;
}
.cartelera-linea {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(204, 0, 0, 0.45));
}
.cartelera-linea--right {
  background: linear-gradient(90deg, rgba(204, 0, 0, 0.45), transparent);
}
.cartelera-titulo-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.cartelera-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(180, 185, 195, 0.75);
}
.cartelera-titulo {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 2px;
  margin: 0;
}
.cartelera-titulo em {
  font-style: normal;
  color: transparent;
  -webkit-text-stroke: 1.5px #cc0000;
}

/* ── Grupo de categoría ── */
.categoria-grupo {
  margin-bottom: 40px;
}

/* ── Separador de categoría ── */
.cat-sep {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 16px;
}
.cat-sep-linea {
  flex: 1;
  height: 2px;
}
.cat-sep-linea--izq {
  border-radius: 2px 0 0 2px;
}
.cat-sep-linea--der {
  border-radius: 0 2px 2px 0;
}

/* Colores por categoría */
.cat-sep-linea--estelar.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #7a0000, #cc0000);
}
.cat-sep-linea--estelar.cat-sep-linea--der {
  background: linear-gradient(90deg, #cc0000, #7a0000, transparent);
}
.cat-sep-label--estelar {
  background: linear-gradient(135deg, #1a0000, #2a0000);
  border: 1px solid rgba(204, 0, 0, 0.65);
  color: #cc0000;
  text-shadow: 0 0 12px rgba(204, 0, 0, 0.4);
  box-shadow: 0 0 20px rgba(204, 0, 0, 0.15);
}
.cat-sep-linea--coestelar.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #5a0000, #990000);
}
.cat-sep-linea--coestelar.cat-sep-linea--der {
  background: linear-gradient(90deg, #990000, #5a0000, transparent);
}
.cat-sep-label--coestelar {
  background: linear-gradient(135deg, #150000, #220000);
  border: 1px solid rgba(204, 0, 0, 0.45);
  color: #aa0000;
  text-shadow: 0 0 10px rgba(204, 0, 0, 0.3);
}
.cat-sep-linea--main_card.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #004d99, #0077cc);
}
.cat-sep-linea--main_card.cat-sep-linea--der {
  background: linear-gradient(90deg, #0077cc, #004d99, transparent);
}
.cat-sep-label--main_card {
  background: linear-gradient(135deg, #001122, #001a33);
  border: 1px solid rgba(0, 119, 204, 0.5);
  color: #4db8ff;
}
.cat-sep-linea--preliminar.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #334455, #556677);
}
.cat-sep-linea--preliminar.cat-sep-linea--der {
  background: linear-gradient(90deg, #556677, #334455, transparent);
}
.cat-sep-label--preliminar {
  background: linear-gradient(135deg, #0a0f14, #111820);
  border: 1px solid rgba(85, 102, 119, 0.5);
  color: rgba(180, 200, 220, 0.85);
}
.cat-sep-linea--teens.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #00aa55, #00cc66);
}
.cat-sep-linea--teens.cat-sep-linea--der {
  background: linear-gradient(90deg, #00cc66, #00aa55, transparent);
}
.cat-sep-label--teens {
  background: linear-gradient(135deg, #001a0d, #00261a);
  border: 1px solid rgba(0, 170, 85, 0.55);
  color: #00dd77;
  text-shadow: 0 0 10px rgba(0, 204, 102, 0.3);
}
.cat-sep-linea--kids.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #6633cc, #9955ff);
}
.cat-sep-linea--kids.cat-sep-linea--der {
  background: linear-gradient(90deg, #9955ff, #6633cc, transparent);
}
.cat-sep-label--kids {
  background: linear-gradient(135deg, #0f0022, #1a0033);
  border: 1px solid rgba(153, 85, 255, 0.55);
  color: #bb88ff;
}
.cat-sep-label {
  font-family: "Oswald", sans-serif;
  font-size: clamp(0.75rem, 1.5vw, 0.95rem);
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  padding: 5px 22px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ══ LISTA CENTRADA: un combate por renglón ════════════════════════════════ */
.combates-grid {
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: 780px;
  margin: 0 auto;
}

/* Wrapper externo: posiciona el badge SIN backdrop-filter */
.combate-wrap {
  position: relative;
  padding-top: 12px; /* espacio para que el badge no se corte arriba */
}

/* ── Fila de combate ── */
.combate-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  background: rgba(10, 2, 2, 0.72);
  border: 1px solid rgba(204, 0, 0, 0.18);
  border-radius: 12px;
  padding: 14px 18px;
  backdrop-filter: blur(14px);
  transition: background 0.2s, border-color 0.2s;
}
.combate-row:hover {
  background: rgba(204, 0, 0, 0.08);
  border-color: rgba(204, 0, 0, 0.38);
}
.combate-row--estelar {
  border-color: rgba(204, 0, 0, 0.42);
  background: rgba(30, 2, 2, 0.8);
}
.combate-row--coestelar {
  border-color: rgba(204, 0, 0, 0.24);
}

/* Badge "Pelea 01" — arriba centrado, relativo al combate-wrap (sin backdrop-filter) */
.combate-num {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(10, 2, 2, 0.95);
  border: 1px solid rgba(204, 0, 0, 0.35);
  color: rgba(204, 0, 0, 0.85);
  font-family: "Oswald", sans-serif;
  font-size: 0.52rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 2px 10px;
  border-radius: 20px;
  white-space: nowrap;
  z-index: 2;
}
.combate-wrap:has(.combate-row--estelar) .combate-num {
  background: rgba(30, 2, 2, 0.95);
  border-color: rgba(204, 0, 0, 0.6);
  color: #cc0000;
}

/* ══ CELDAS DE PELEADOR ═══════════════════════════════════════════════════
   Cada celda ocupa su columna del grid y distribuye foto + info internamente.
   Izquierda: foto a la izquierda, info a la derecha  →  flex-row normal
   Derecha:   info a la izquierda, foto a la derecha  →  flex-row normal
              (el orden del DOM ya es info primero, foto segundo)
   ════════════════════════════════════════════════════════════════════════ */
.peleador-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

/* Izquierda: foto | info — sin cambios */
.peleador-cell--izq {
  flex-direction: row;
  justify-content: flex-start;
}

/* Derecha: info | foto — el DOM ya viene en este orden, solo alineamos al final */
.peleador-cell--der {
  flex-direction: row;
  justify-content: flex-end;
}

/* Foto rectangular con esquinas redondeadas */
.peleador-foto-wrap {
  flex-shrink: 0;
  width: 64px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(204, 0, 0, 0.28);
  background: rgba(204, 0, 0, 0.04);
}
.combate-row--estelar .peleador-foto-wrap {
  width: 72px;
  height: 90px;
  border-color: rgba(204, 0, 0, 0.48);
  border-radius: 14px;
}
.peleador-foto {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
}
.peleador-foto-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Oswald", sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: rgba(204, 0, 0, 0.55);
  background: rgba(204, 0, 0, 0.05);
}

/* Info del peleador */
.peleador-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}
.peleador-info--izq {
  align-items: flex-start;
  text-align: left;
}
.peleador-info--der {
  align-items: flex-end;
  text-align: right;
}
.peleador-nombre {
  font-family: "Oswald", sans-serif;
  font-size: clamp(0.78rem, 1.2vw, 0.95rem);
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.2;
  word-break: break-word;
}
.combate-row--estelar .peleador-nombre {
  font-size: clamp(0.85rem, 1.3vw, 1rem);
  color: #fff5f5;
}
.peleador-equipo {
  font-size: 0.62rem;
  color: rgba(180, 185, 195, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.peleador-nac {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.58rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
}
/* Bandera derecha: bandera a la derecha del texto */
.peleador-nac--der {
  flex-direction: row-reverse;
}
.bandera-img {
  width: 20px;
  height: 14px;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

/* ── VS central ── */
.vs-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 44px;
  border-left: 1px solid rgba(204, 0, 0, 0.1);
  border-right: 1px solid rgba(204, 0, 0, 0.1);
  padding: 0 8px;
  align-self: stretch;
}
.vs-texto {
  font-family: "Oswald", sans-serif;
  font-size: 1.1rem;
  font-weight: 800;
  color: transparent;
  -webkit-text-stroke: 1.5px #cc0000;
  letter-spacing: 2px;
  line-height: 1;
  filter: drop-shadow(0 0 8px rgba(204, 0, 0, 0.35));
}

/* ── CTA ── */
.cartelera-cta {
  display: flex;
  justify-content: center;
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid rgba(204, 0, 0, 0.1);
}

/* ── Responsive cartelera ── */
@media (max-width: 860px) {
  .combates-grid {
    max-width: 600px;
  }
  .peleador-nombre {
    font-size: 0.8rem;
  }
  .peleador-foto-wrap {
    width: 56px;
    height: 70px;
  }
  .combate-row--estelar .peleador-foto-wrap {
    width: 62px;
    height: 78px;
  }
}

/* ── Móvil: layout tipo card (imagen → nac → nombre) ── */
/* En desktop mostramos los elementos --desktop y ocultamos --movil */
.peleador-card--movil {
  display: none;
}
.peleador-foto-wrap--desktop,
.peleador-info--desktop {
  display: flex;
}

@media (max-width: 600px) {
  .combates-grid {
    max-width: 100%;
  }
  .cartelera-wrap {
    padding: 48px 0 56px;
  }
  .combate-row {
    padding: 18px 10px 16px;
    gap: 4px;
  }

  /* Ocultar elementos desktop, mostrar cards móvil */
  .peleador-foto-wrap--desktop,
  .peleador-info--desktop {
    display: none !important;
  }
  .peleador-card--movil {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  /* Ambas celdas en columna centrada */
  .peleador-cell--izq,
  .peleador-cell--der {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 0;
  }

  /* Foto grande en móvil — overflow visible para mostrar bandera superpuesta */
  .peleador-card--movil .peleador-foto-wrap {
    width: 90px;
    height: 112px;
    border-radius: 12px;
    flex-shrink: 0;
    position: relative;
    overflow: visible; /* bandera sobresale del borde */
  }
  /* La imagen sí debe estar clipeada al borde redondeado */
  .peleador-card--movil .peleador-foto {
    border-radius: 12px;
    overflow: hidden;
  }
  .combate-row--main-card .peleador-card--movil .peleador-foto-wrap {
    width: 94px;
    height: 118px;
    border-radius: 13px;
  }
  .combate-row--main-card .peleador-card--movil .peleador-foto {
    border-radius: 13px;
  }

  /* Bandera superpuesta sobre la imagen */
  .bandera-overlay {
    position: absolute;
    bottom: -1px;
    display: flex;
    align-items: center;

    z-index: 3;
  }
  /* Peleador izquierdo: esquina inferior izquierda */
  .bandera-overlay--izq {
    left: -1px;
  }
  /* Peleador derecho: esquina inferior derecha */
  .bandera-overlay--der {
    right: -1px;
  }
  .bandera-overlay-img {
    width: 30px;
    height: 20px;
    border-radius: 3px;
    object-fit: cover;
    display: block;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  }

  /* Nombre en móvil */
  .peleador-nombre--movil {
    font-family: "Oswald", sans-serif;
    font-size: 0.7rem;
    font-weight: 600;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    line-height: 1.2;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    max-width: 96px;
  }
  .combate-row--main-card .peleador-nombre--movil {
    color: #fff5e6;
    font-weight: 700;
  }

  .vs-texto {
    font-size: 1rem;
  }
  .cat-sep-label {
    font-size: 0.65rem;
    letter-spacing: 2.5px;
    padding: 4px 14px;
  }
  .cartelera-header {
    gap: 12px;
  }
}

/* ══ MAIN CARD — diseño premium rojo/dorado ════════════════════════════════ */

/* Separador: líneas rojo → acento dorado */
.cat-sep-linea--main_card.cat-sep-linea--izq {
  background: linear-gradient(90deg, transparent, #6a0000, #990000, #cc0000);
}
.cat-sep-linea--main_card.cat-sep-linea--der {
  background: linear-gradient(90deg, #cc0000, #990000, #6a0000, transparent);
}
.cat-sep-label--main_card {
  background: linear-gradient(135deg, #120000, #200000);
  border: 1px solid rgba(204, 0, 0, 0.75);
  color: #ff3333;
  text-shadow: 0 0 14px rgba(204, 0, 0, 0.6);
  box-shadow: 0 0 22px rgba(204, 0, 0, 0.2), inset 0 0 10px rgba(204, 0, 0, 0.06);
  letter-spacing: 5px;
}

/* Fila de combate Main Card */
.combate-row--main-card {
  background: linear-gradient(135deg, rgba(28, 6, 2, 0.92) 0%, rgba(12, 3, 1, 0.95) 100%);
  border: 1px solid rgba(204, 0, 0, 0.38);
  border-radius: 14px;
  box-shadow: 0 0 0 1px rgba(204, 0, 0, 0.07), 0 8px 32px rgba(0, 0, 0, 0.65),
    inset 0 1px 0 rgba(204, 0, 0, 0.08);
  padding: 18px 22px;
  position: relative;
  overflow: hidden;
}

/* Línea decorativa rojo→dorado en la parte superior */
.combate-row--main-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 8%;
  right: 8%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(204, 0, 0, 0.55),
    rgba(220, 60, 60, 0.5),
    rgba(204, 0, 0, 0.55),
    transparent
  );
}

/* Hover */
.combate-row--main-card:hover {
  background: linear-gradient(135deg, rgba(38, 9, 3, 0.95) 0%, rgba(18, 5, 2, 0.98) 100%);
  border-color: rgba(204, 0, 0, 0.6);
  box-shadow: 0 0 0 1px rgba(204, 0, 0, 0.12), 0 12px 40px rgba(0, 0, 0, 0.7),
    0 0 36px rgba(204, 0, 0, 0.08);
}

/* Badge "Pelea XX" rojo con toque dorado */
.combate-wrap:has(.combate-row--main-card) .combate-num {
  background: linear-gradient(135deg, #1a0400, #2a0800);
  border-color: rgba(204, 0, 0, 0.65);
  color: #ff3333;
  text-shadow: 0 0 10px rgba(204, 0, 0, 0.5);
  box-shadow: 0 0 12px rgba(204, 0, 0, 0.2);
}

/* Foto más grande con borde rojo/dorado */
.combate-row--main-card .peleador-foto-wrap {
  width: 76px;
  height: 94px;
  border-color: rgba(204, 0, 0, 0.5);
  border-radius: 13px;
  box-shadow: 0 0 12px rgba(204, 0, 0, 0.22), 0 0 4px rgba(180, 185, 195, 0.1),
    0 4px 14px rgba(0, 0, 0, 0.55);
}

/* Nombre más destacado */
.combate-row--main-card .peleador-nombre {
  font-size: clamp(0.82rem, 1.3vw, 1rem);
  color: #fff2f2;
  font-weight: 700;
}

/* Equipo con acento rojo suave */
.combate-row--main-card .peleador-equipo {
  color: rgba(180, 185, 195, 0.6);
}

/* VS rojo con destello dorado */
.combate-row--main-card .vs-texto {
  color: transparent;
  -webkit-text-stroke: 1.5px #cc0000;
  filter: drop-shadow(0 0 8px rgba(204, 0, 0, 0.5))
    drop-shadow(0 0 2px rgba(180, 185, 195, 0.1));
  font-size: 1.2rem;
}

/* Divisores del VS en rojo suave */
.combate-row--main-card .vs-block {
  border-left-color: rgba(204, 0, 0, 0.15);
  border-right-color: rgba(204, 0, 0, 0.15);
}
</style>
